import { useEffect } from 'react';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';

/**
 * Heading sizes relative to the TechDocs body font size
 * (`--md-typeset-font-size`). TechDocs derives content heading sizes from the
 * MUI theme typography with a fixed 0.6 factor, which makes `h2`-`h6` render
 * smaller than surrounding text with this theme. This restores a readable
 * hierarchy without altering the app-wide MUI typography.
 */
const HEADING_SCALE: Record<string, number> = {
  h1: 2,
  h2: 1.5,
  h3: 1.25,
  h4: 1.1,
  h5: 1,
  h6: 0.85,
};

const TechDocsHeadingSizeAddon = () => {
  const [head] = useShadowRootElements<HTMLHeadElement>(['head']);

  useEffect(() => {
    if (!head) {
      return undefined;
    }

    const style = document.createElement('style');
    style.dataset.spinTechdocsHeadings = 'true';
    style.textContent = Object.entries(HEADING_SCALE)
      .map(
        ([tag, scale]) =>
          `.md-typeset ${tag} { font-size: calc(${scale} * var(--md-typeset-font-size)) !important; }`,
      )
      .join('\n');
    head.appendChild(style);

    return () => {
      style.remove();
    };
  }, [head]);

  return null;
};

const techdocsSpinAddonsPlugin = createPlugin({
  id: 'techdocs-spin-addons',
});

export const TechDocsHeadingSize = techdocsSpinAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsHeadingSize',
    location: TechDocsAddonLocations.Content,
    component: TechDocsHeadingSizeAddon,
  }),
);
